<template>
  <div class="step-content">
    <h5 class="mb-3 printable-item" v-if="!disabled">Revisão dos Dados</h5>
    <h5 class="mb-3 printable-item" v-else>CEBAS - Dados da Solicitação de Credenciamento</h5>

    <b-alert variant="warning" show class="mb-4" v-if="!disabled">
      <div class="alert-body">
        Revise cuidadosamente todas as informações antes de enviar sua solicitação.
      </div>
    </b-alert>

    <!-- Dados Básicos -->
    <b-card class="mb-3">
      <template #header>
        <h6 class="mb-0 printable-item">Dados Básicos</h6>
      </template>
      <b-row>
        <b-col md="6">
          <span class="printable-item"><strong>Tipo de Credenciamento:</strong> {{ getCredentialingTypeLabel(basicData.credentialing_type) }}</span><br>
          <span class="printable-item"><strong>Área de Atuação:</strong> {{ getActivityAreaLabel(basicData.activity_area) }}</span><br>
          <span class="printable-item"><strong>Nome da Entidade:</strong> {{ basicData.entity_name }}</span><br>
          <span class="printable-item"><strong>CNPJ:</strong> {{ basicData.cnpj }}</span><br>
          <span class="printable-item"><strong>CNAE Principal:</strong> {{ basicData.main_cnae }}</span>
        </b-col>
        <b-col md="6">
          <span class="printable-item"><strong>E-mail:</strong> {{ basicData.email }}</span><br>
          <span class="printable-item"><strong>Telefone:</strong> {{ basicData.phone }}</span><br>
          <span class="printable-item"><strong>Endereço:</strong> <span class="whitespace-pre-wrap">{{ basicData.address }}</span></span>
        </b-col>
      </b-row>

      <hr class="my-3">

      <div class="legal-representative-section mt-3">
        <h6 class="text-muted mb-2 printable-item">Representante Legal</h6>
        <p class="mb-1 printable-item"><strong>Nome:</strong> {{ basicData.legal_representative_name }}</p>
        <p class="mb-1 printable-item"><strong>CPF:</strong> {{ basicData.legal_representative_cpf }}</p>
        <p class="mb-1 printable-item"><strong>Cargo:</strong> {{ basicData.legal_representative_position }}</p>
        <p class="mb-0 printable-item"><strong>E-mail:</strong> {{ basicData.legal_representative_email }}</p>
      </div>
    </b-card>

    <!-- Documentação Jurídica -->
    <b-card class="mb-3">
      <template #header>
        <h6 class="mb-0 printable-item">Documentação Jurídica</h6>
      </template>
      <div v-if="hasJuridicalDocuments">
        <ul class="list-unstyled mb-0">
          <li v-if="juridicalDocumentation.statute" class="mb-2 printable-item">
            <i class="feather icon-file" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getJuridicalDocumentUrl(juridicalDocumentation.statute)" :href="getJuridicalDocumentUrl(juridicalDocumentation.statute)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Estatuto / Ato Constitutivo:</strong>
            {{ getFileName(juridicalDocumentation.statute) }}
            <small v-if="getFileSize(juridicalDocumentation.statute)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(juridicalDocumentation.statute)) }})
            </small>
          </li>
          <li v-if="juridicalDocumentation.election_minutes" class="mb-2 printable-item">
            <i class="feather icon-file" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getJuridicalDocumentUrl(juridicalDocumentation.election_minutes)" :href="getJuridicalDocumentUrl(juridicalDocumentation.election_minutes)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Ata de Eleição de Dirigentes:</strong>

            {{ getFileName(juridicalDocumentation.election_minutes) }}
            <small v-if="getFileSize(juridicalDocumentation.election_minutes)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(juridicalDocumentation.election_minutes)) }})
            </small>
          </li>
          <li v-if="juridicalDocumentation.foundation_document" class="mb-2 printable-item">
            <i class="feather icon-file" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getJuridicalDocumentUrl(juridicalDocumentation.foundation_document)" :href="getJuridicalDocumentUrl(juridicalDocumentation.foundation_document)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Escritura Pública ou Testamento:</strong>

            {{ getFileName(juridicalDocumentation.foundation_document) }}
            <small v-if="getFileSize(juridicalDocumentation.foundation_document)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(juridicalDocumentation.foundation_document)) }})
            </small>
          </li>
          <li v-if="juridicalDocumentation.cnpj_certificate" class="mb-2 printable-item">
            <i class="feather icon-file" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getJuridicalDocumentUrl(juridicalDocumentation.cnpj_certificate)" :href="getJuridicalDocumentUrl(juridicalDocumentation.cnpj_certificate)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Comprovante de CNPJ:</strong>
            {{ getFileName(juridicalDocumentation.cnpj_certificate) }}
            <small v-i
            f="getFileSize(juridicalDocumentation.cnpj_certificate)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(juridicalDocumentation.cnpj_certificate)) }})
            </small>
          </li>
          <li v-if="juridicalDocumentation.identification_members" class="mb-0 printable-item">
            <i class="feather icon-file" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getJuridicalDocumentUrl(juridicalDocumentation.identification_members)" :href="getJuridicalDocumentUrl(juridicalDocumentation.identification_members)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Identificação dos integrantes do corpo dirigente de cada instituição:</strong>
            {{ getFileName(juridicalDocumentation.identification_members) }}
            <small v-if="getFileSize(juridicalDocumentation.identification_members)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(juridicalDocumentation.identification_members)) }})
            </small>
          </li>
        </ul>
      </div>
      <div v-else class="text-muted printable-item">
        <i class="feather icon-alert-triangle mr-2"></i>
        Nenhum documento jurídico foi anexado ainda
      </div>
    </b-card>

    <!-- Dados Econômicos e Operacionais -->
    <b-card class="mb-3">
      <template #header>
        <h6 class="mb-0 printable-item">Dados Econômicos e Operacionais</h6>
      </template>
      <b-row>
        <b-col md="6">
          <span class="printable-item"><strong>Período de Referência - Início:</strong> {{ formatDate(economicOperationalData.certificate_period_start) }}</span><br>
          <span class="printable-item"><strong>Período de Referência - Fim:</strong> {{ formatDate(economicOperationalData.certificate_period_end) }}</span>
        </b-col>
        <b-col md="6">
          <span class="printable-item"><strong>Número de Bolsas Ofertadas:</strong> {{ economicOperationalData.scholarship_count }}</span><br>
          <span class="printable-item"><strong>Tipos de Bolsas:</strong> {{ getScholarshipTypesLabel(economicOperationalData.scholarship_types) }}</span>
        </b-col>
      </b-row>

      <div v-if="economicOperationalData.support_services" class="mt-3">
        <hr class="my-3">
        <h6 class="text-muted mb-2 printable-item">Serviços de Apoio aos Bolsistas</h6>
        <p class="mb-0 printable-item whitespace-pre-wrap">{{ economicOperationalData.support_services }}</p>
      </div>

      <!-- Documentação dos Dados Econômicos Operacionais -->
      <div v-if="hasEconomicDocuments" class="mt-3">
        <hr class="my-2">
        <h6 class="text-muted mb-2 printable-item">Documentação</h6>
        <ul class="list-unstyled mb-0">
          <li v-if="economicOperationalData.report_activities" class="mb-2 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.report_activities)" :href="getDownloadUrl(economicOperationalData.report_activities)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Relatório de Atividades Desempenhadas:</strong>
            {{ getFileName(economicOperationalData.report_activities) }}
            <small v-if="getFileSize(economicOperationalData.report_activities)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.report_activities)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.summary_report_scholarships" class="mb-2 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.summary_report_scholarships)" :href="getDownloadUrl(economicOperationalData.summary_report_scholarships)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Relatório Sintético de Concessão de Bolsas:</strong>
            {{ getFileName(economicOperationalData.summary_report_scholarships) }}
            <small v-if="getFileSize(economicOperationalData.summary_report_scholarships)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.summary_report_scholarships)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.list_scholarships" class="mb-2 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.list_scholarships)" :href="getDownloadUrl(economicOperationalData.list_scholarships)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Relação de Bolsas de Estudo:</strong>
            {{ getFileName(economicOperationalData.list_scholarships) }}
            <small v-if="getFileSize(economicOperationalData.list_scholarships)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.list_scholarships)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.analysis_socioeconomic_students" class="mb-2 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.analysis_socioeconomic_students)" :href="getDownloadUrl(economicOperationalData.analysis_socioeconomic_students)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Análise do Perfil Socioeconômico dos Alunos Bolsistas:</strong>
            {{ getFileName(economicOperationalData.analysis_socioeconomic_students) }}
            <small v-if="getFileSize(economicOperationalData.analysis_socioeconomic_students)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.analysis_socioeconomic_students)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.accounting_regime" class="mb-2 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.accounting_regime)" :href="getDownloadUrl(economicOperationalData.accounting_regime)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Regime Contábil:</strong>
            {{ getFileName(economicOperationalData.accounting_regime) }}
            <small v-if="getFileSize(economicOperationalData.accounting_regime)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.accounting_regime)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.accounting_reports" class="mb-2 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.accounting_reports)" :href="getDownloadUrl(economicOperationalData.accounting_reports)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Relatórios Contábeis:</strong>
            {{ getFileName(economicOperationalData.accounting_reports) }}
            <small v-if="getFileSize(economicOperationalData.accounting_reports)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.accounting_reports)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.accounting_gratuity_demo" class="mb-2 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.accounting_gratuity_demo)" :href="getDownloadUrl(economicOperationalData.accounting_gratuity_demo)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Demonstração Contábil da Gratuidade:</strong>
            {{ getFileName(economicOperationalData.accounting_gratuity_demo) }}
            <small v-if="getFileSize(economicOperationalData.accounting_gratuity_demo)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.accounting_gratuity_demo)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.audit_opinion" class="mb-2 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.audit_opinion)" :href="getDownloadUrl(economicOperationalData.audit_opinion)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Parecer de Auditoria Independente do Exercício em Análise:</strong>
            {{ getFileName(economicOperationalData.audit_opinion) }}
            <small v-if="getFileSize(economicOperationalData.audit_opinion)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.audit_opinion)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.revenue_received" class="mb-0 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.revenue_received)" :href="getDownloadUrl(economicOperationalData.revenue_received)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Receita Efetivamente Recebida:</strong>
            {{ getFileName(economicOperationalData.revenue_received) }}
            <small v-if="getFileSize(economicOperationalData.revenue_received)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.revenue_received)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.service_plan" class="mb-0 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.service_plan)" :href="getDownloadUrl(economicOperationalData.service_plan)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Plano de Atendimento:</strong>
            {{ getFileName(economicOperationalData.service_plan) }}
            <small v-if="getFileSize(economicOperationalData.service_plan)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.service_plan)) }})
            </small>
          </li>
          <li v-if="economicOperationalData.social_assistance_registration" class="mb-0 printable-item">
            <i class="feather icon-file-text" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"></i>
            <a v-if="getDownloadUrl(economicOperationalData.social_assistance_registration)" :href="getDownloadUrl(economicOperationalData.social_assistance_registration)" target="_blank" class="ml-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong class="ml-2">Inscrição dos Serviços Socioassistenciais:</strong>
            {{ getFileName(economicOperationalData.social_assistance_registration) }}
            <small v-if="getFileSize(economicOperationalData.social_assistance_registration)" class="text-muted ml-2">
              ({{ formatFileSize(getFileSize(economicOperationalData.social_assistance_registration)) }})
            </small>
          </li>
        </ul>
      </div>
      <div v-else class="mt-3">
        <hr class="my-2">
        <h6 class="text-muted mb-2 printable-item">Documentação</h6>
        <div class="text-muted printable-item">
          <i class="feather icon-alert-triangle mr-2"></i>
          Nenhum documento foi anexado ainda
        </div>
      </div>
    </b-card>

    <!-- Unidades Vinculadas -->
    <b-card class="mb-3">
      <template #header>
        <h6 class="mb-0 printable-item">Unidades Vinculadas</h6>
      </template>
      <div v-if="hasLinkedInstitutions">
        <!-- Lista de Unidades Vinculadas -->
        <div v-for="(institution, index) in linkedInstitutions" :key="index" class="mb-4">
          <div v-if="index > 0" class="hr-divider my-3">
            <hr>
          </div>

          <h6 class="text-muted mb-2 printable-item">
            <i class="feather icon-building mr-2"></i>
            Unidade {{ index + 1 }}
          </h6>

          <b-row>
            <b-col md="12" v-if="institution.institution_name">
              <span class="printable-item"><strong>Nome da Instituição:</strong> {{ institution.institution_name }}</span><br>
            </b-col>
            <b-col md="6" v-if="institution.institution_cnpj">
              <span class="printable-item"><strong>CNPJ:</strong> {{ institution.institution_cnpj }}</span><br>
            </b-col>
            <b-col md="6" v-if="institution.city_name || institution.state_name || institution.state_short">
              <span class="printable-item"><strong>Localização:</strong>
              {{ institution.city_name || '' }}{{ (institution.city_name && (institution.state_short || institution.state_name)) ? ', ' : '' }}{{ institution.state_short || institution.state_name || '' }}</span><br>
            </b-col>
          </b-row>

          <!-- Documento de Autorização -->
          <div v-if="institution.institution_authorization || institution.school_regulations" class="mt-3">
            <hr class="my-2">
            <h6 class="text-muted mb-2 printable-item">Documentação</h6>
            <ul class="list-unstyled mb-0">
              <li v-if="institution.institution_authorization" class="mb-2 printable-item">
                <i class="feather icon-file-text text-primary mr-2"></i>
                <a v-if="getDownloadUrl(institution.institution_authorization)" :href="getDownloadUrl(institution.institution_authorization)" target="_blank" class="mr-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
                  <i class="feather icon-download"></i>
                </a>
                <strong>Atos de Credenciamento/Autorização:</strong>
                {{ getFileName(institution.institution_authorization) }}
                <small v-if="getFileSize(institution.institution_authorization)" class="text-muted ml-2">
                  ({{ formatFileSize(getFileSize(institution.institution_authorization)) }})
                </small>
              </li>
              <li v-if="institution.school_regulations" class="mb-2 printable-item">
                <i class="feather icon-file-text text-primary mr-2"></i>
                <a v-if="getDownloadUrl(institution.school_regulations)" :href="getDownloadUrl(institution.school_regulations)" target="_blank" class="mr-1 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
                  <i class="feather icon-download"></i>
                </a>
                <strong>Regimento Interno ou Estatuto:</strong>
                {{ getFileName(institution.school_regulations) }}
                <small v-if="getFileSize(institution.school_regulations)" class="text-muted ml-2">
                  ({{ formatFileSize(getFileSize(institution.school_regulations)) }})
                </small>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div v-else class="text-muted printable-item">
        <i class="feather icon-alert-triangle mr-2"></i>
        Nenhuma unidade vinculada foi informada ainda
      </div>
    </b-card>

    <!-- Anexos Complementares -->
    <b-card class="mb-3">
      <template #header>
        <h6 class="mb-0 printable-item">Anexos Complementares</h6>
      </template>
      <div v-if="hasAdditionalAttachments">
        <p class="text-muted mb-3 printable-item">
          <i class="feather icon-paperclip mr-1"></i>
          {{ additionalAttachments.additional_attachments.length }} anexo(s) complementar(es)
        </p>
        <ul class="list-unstyled mb-0">
          <li
            v-for="(file, index) in additionalAttachments.additional_attachments"
            :key="index"
            class="mb-2 printable-item"
          >
            <i :class="getFileIcon(getFileName(file))" class="mr-2"></i>
            <a v-if="getAttachmentUrl(file)" :href="getAttachmentUrl(file)" target="_blank" class="mr-2 no-print download-link" :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'" title="Baixar arquivo">
              <i class="feather icon-download"></i>
            </a>
            <strong>{{ getFileName(file) }}</strong>
            <small class="text-muted ml-2">({{ formatFileSize(getFileSize(file)) }})</small>


          </li>
        </ul>
      </div>
      <div v-else class="text-muted printable-item">
        <i class="feather icon-info mr-2"></i>
        Nenhum anexo complementar foi adicionado (opcional)
      </div>
    </b-card>

    <b-form-group class="mt-4" v-if="!disabled">
      <b-form-checkbox
        :checked="acceptTerms"
        :state="getFieldState('acceptTerms')"
        :class="!primaryVariant ? 'checkbox-dynamic-primary' : ''"
        @change="$emit('update:accept-terms', $event)"
      >
        Declaro que todas as informações fornecidas são verdadeiras e aceito os
        <b-link
          href="#"
          :class="primaryVariant ? `text-${primaryVariant}` : 'text-dynamic-primary'"
          @click="$emit('show-terms')"
        >
          termos e condições
        </b-link>.
      </b-form-checkbox>
      <b-form-invalid-feedback>
        Você deve aceitar os termos e condições
      </b-form-invalid-feedback>
    </b-form-group>

    <!-- Botão de Imprimir -->
    <div class="print-button-container text-center mt-4 no-print cursor-pointer">
      <b-button
        :variant="primaryVariant || 'primary'"
        :class="!primaryVariant ? 'btn-dynamic-primary' : ''"
        size="lg"
        @click="printPage"
        class="mr-1"
      >
        <i class="feather icon-printer mr-1"></i>
        Imprimir Revisão
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormCheckbox,
  BFormInvalidFeedback,
  BAlert,
  BCard,
  BLink
} from 'bootstrap-vue'

export default {
  name: 'ReviewStep',
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormCheckbox,
    BFormInvalidFeedback,
    BAlert,
    BCard,
    BLink
  },
  props: {
    basicData: {
      type: Object,
      required: true
    },
    juridicalDocumentation: {
      type: Object,
      required: true
    },
    economicOperationalData: {
      type: Object,
      required: true
    },
    linkedInstitutions: {
      type: Array,
      required: true,
      default: () => []
    },
    additionalAttachments: {
      type: Object,
      required: true
    },
    acceptTerms: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    activityAreas: {
      type: Array,
      required: true
    },
    organizationSizes: {
      type: Array,
      required: true
    },
    primaryVariant: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:accept-terms', 'show-terms'],
  data() {
    return {
      elementsHiddenForPrint: []
    };
  },
  computed: {
    hasJuridicalDocuments() {
      return !!(
        this.juridicalDocumentation.statute ||
        this.juridicalDocumentation.election_minutes ||
        this.juridicalDocumentation.foundation_document ||
        this.juridicalDocumentation.cnpj_certificate ||
        this.juridicalDocumentation.identification_members
      );
    },
    hasLinkedInstitutions() {
      return Array.isArray(this.linkedInstitutions) && this.linkedInstitutions.length > 0;
    },
    hasAdditionalAttachments() {
      return !!(
        this.additionalAttachments.additional_attachments &&
        this.additionalAttachments.additional_attachments.length > 0
      );
    },
    hasEconomicDocuments() {
      return !!(
        this.economicOperationalData.report_activities ||
        this.economicOperationalData.summary_report_scholarships ||
        this.economicOperationalData.list_scholarships ||
        this.economicOperationalData.analysis_socioeconomic_students ||
        this.economicOperationalData.accounting_regime ||
        this.economicOperationalData.accounting_reports ||
        this.economicOperationalData.accounting_gratuity_demo ||
        this.economicOperationalData.audit_opinion ||
        this.economicOperationalData.revenue_received ||
        this.economicOperationalData.service_plan ||
        this.economicOperationalData.social_assistance_registration
      );
    }
  },
  methods: {
    getFieldState(fieldName) {
      if (this.errors[fieldName]) {
        return false;
      }
      return null;
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('pt-BR');
    },
    getActivityAreaLabel(value) {
      const activityAreaOptions = [
        { text: 'Educação', value: 'ED' },
        { text: 'Saúde', value: 'HE' },
        { text: 'Assistência Social', value: 'SA' }
      ];

      const area = activityAreaOptions.find(area => area.value === value);
      return area ? area.text : value;
    },
    getActivityAreaLabels(values) {
      if (!values || !Array.isArray(values) || values.length === 0) {
        return '';
      }

      const activityAreaOptions = [
        { text: 'Educação', value: 'education' },
        { text: 'Saúde', value: 'health' },
        { text: 'Assistência Social', value: 'social_assistance' }
      ];

      const labels = values.map(value => {
        const area = activityAreaOptions.find(area => area.value === value);
        return area ? area.text : value;
      });

      return labels.join(', ');
    },
    getOrganizationSizeLabel(value) {
      const size = this.organizationSizes.find(size => size.value === value);
      return size ? size.text : value;
    },
    getCredentialingTypeLabel(value) {
      const credentialingTypeOptions = [
        { text: 'Credenciamento', value: 'CR' },
        { text: 'Recredenciamento', value: 'RC' }
      ];

      const type = credentialingTypeOptions.find(type => type.value === value);
      return type ? type.text : value;
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getFileIcon(fileName) {
      if (!fileName || typeof fileName !== 'string') {
        return 'feather icon-file text-muted';
      }

      const extension = fileName.split('.').pop().toLowerCase();
      const iconMap = {
        'pdf': 'feather icon-file-text text-danger',
        'doc': 'feather icon-file-text text-primary',
        'docx': 'feather icon-file-text text-primary',
        'xls': 'feather icon-file text-success',
        'xlsx': 'feather icon-file text-success',
        'jpg': 'feather icon-image text-info',
        'jpeg': 'feather icon-image text-info',
        'png': 'feather icon-image text-info'
      };

      return iconMap[extension] || 'feather icon-file text-muted';
    },
    getScholarshipTypesLabel(values) {
      if (!values) {
        return '';
      }

      // Se é uma string (valor da API: IT, PT, ou BO)
      if (typeof values === 'string') {
        switch (values) {
          case 'IT':
            return 'Integral (100%)';
          case 'PT':
            return 'Parcial';
          case 'BO':
            return 'Integral (100%), Parcial';
          default:
            return values;
        }
      }

      // Se é um array (compatibilidade com versões anteriores)
      if (Array.isArray(values)) {
        if (values.length === 0) {
          return '';
        }

        const scholarshipTypeOptions = [
          { text: 'Integral (100%)', value: 'integral' },
          { text: 'Integral (100%)', value: 'IT' },
          { text: 'Parcial', value: 'parcial' },
          { text: 'Parcial', value: 'PT' }
        ];

        const labels = values.map(value => {
          const type = scholarshipTypeOptions.find(type => type.value === value);
          return type ? type.text : value;
        });

        return labels.join(', ');
      }

      return '';
    },

    getFileName(file) {
      if (!file) return '';

      // Se é um arquivo que foi enviado (tem metadata)
      if (file.metadata && file.metadata.original_name) {
        return file.metadata.original_name;
      }

      // Se é um arquivo que está sendo enviado (tem name)
      if (file.name) {
        return file.name;
      }

      // Se é um arquivo que foi carregado do servidor (tem file_name)
      if (file.file_name) {
        return file.file_name;
      }

      return 'Arquivo anexado';
    },

    getFileSize(file) {
      if (!file) return null;

      // Se é um arquivo que foi enviado (tem metadata)
      if (file.metadata && file.metadata.size) {
        return file.metadata.size;
      }

      // Se é um arquivo que está sendo enviado (tem size)
      if (file.size) {
        return file.size;
      }

      return null;
    },

    getDownloadUrl(file) {
      if (!file) return null;



      // Se é um arquivo que foi enviado (tem url_tmp)
      if (file.url_tmp) {
        return file.url_tmp;
      }

      // Se é um arquivo que foi carregado do servidor (tem metadata)
      if (file.metadata && file.metadata.url_tmp) {
        return file.metadata.url_tmp;
      }

      // Verificar outras possíveis propriedades de URL
      if (file.url_temp) {
        return file.url_temp;
      }

      if (file.url) {
        return file.url;
      }

      if (file.download_url) {
        return file.download_url;
      }

      return null;
    },

    // Método para obter URL de download dos documentos jurídicos
    getJuridicalDocumentUrl(document) {
      if (!document) return null;



      // Tentar diferentes propriedades de URL - CORRIGIDO: url_tmp primeiro
      if (document.url_tmp) {
        return document.url_tmp;
      }

      if (document.url_temp) {
        return document.url_temp;
      }

      if (document.url) {
        return document.url;
      }

      if (document.download_url) {
        return document.download_url;
      }

      if (document.metadata && document.metadata.url_tmp) {
        return document.metadata.url_tmp;
      }

      if (document.metadata && document.metadata.url_temp) {
        return document.metadata.url_temp;
      }

      return null;
    },

    // Método para debug dos anexos complementares
    getAttachmentUrl(file) {
      if (!file) return null;



      // Tentar diferentes propriedades de URL - CORRIGIDO: url_tmp primeiro
      if (file.url_tmp) {
        return file.url_tmp;
      }

      if (file.url_temp) {
        return file.url_temp;
      }

      if (file.url) {
        return file.url;
      }

      if (file.download_url) {
        return file.download_url;
      }

      return null;
    },

    printPage() {
      // Múltiplas abordagens para encontrar e ocultar os botões do wizard
      const possibleSelectors = [
        '.wizard-card-footer.clearfix',
        '.wizard-card-footer',
        '.vue-form-wizard .wizard-card-footer',
        '.wizard-footer-left',
        '.wizard-footer-right'
      ];

      const elementsToHide = [];

      // Tentar encontrar os elementos usando diferentes seletores
      possibleSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (!elementsToHide.includes(el)) {
            elementsToHide.push(el);
          }
        });
      });

      // Também procurar por botões específicos do wizard
      const wizardButtons = document.querySelectorAll('.wizard-btn, .vue-form-wizard button');
      wizardButtons.forEach(btn => {
        if (!elementsToHide.includes(btn)) {
          elementsToHide.push(btn);
        }
      });

      console.log('Elementos encontrados para ocultar:', elementsToHide.length);

      if (elementsToHide.length > 0) {
        // Adicionar classe no-print a todos os elementos encontrados
        elementsToHide.forEach(el => {
          el.classList.add('no-print');
        });

        setTimeout(() => {
          window.print();

          // Remover classe no-print após a impressão
          setTimeout(() => {
            elementsToHide.forEach(el => {
              el.classList.remove('no-print');
            });
          }, 500);
        }, 100);
      } else {
        console.log('Nenhum elemento de wizard encontrado, usando CSS apenas');
        setTimeout(() => {
          window.print();
        }, 100);
      }
    },

    // Método reutilizável para encontrar e gerenciar elementos do wizard
    findWizardElements() {
      const possibleSelectors = [
        '.wizard-card-footer.clearfix',
        '.wizard-card-footer',
        '.vue-form-wizard .wizard-card-footer',
        '.wizard-footer-left',
        '.wizard-footer-right'
      ];

      const elementsToHide = [];

      // Tentar encontrar os elementos usando diferentes seletores
      possibleSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (!elementsToHide.includes(el)) {
            elementsToHide.push(el);
          }
        });
      });

      // Também procurar por botões específicos do wizard
      const wizardButtons = document.querySelectorAll('.wizard-btn, .vue-form-wizard button');
      wizardButtons.forEach(btn => {
        if (!elementsToHide.includes(btn)) {
          elementsToHide.push(btn);
        }
      });

      return elementsToHide;
    },

    // Event handler para beforeprint (Ctrl+P)
    handleBeforePrint() {
      console.log('Ctrl+P detectado - ocultando elementos do wizard');
      this.elementsHiddenForPrint = this.findWizardElements();

      // Adicionar classe no-print a todos os elementos encontrados
      this.elementsHiddenForPrint.forEach(el => {
        el.classList.add('no-print');
      });
    },

    // Event handler para afterprint (após impressão)
    handleAfterPrint() {
      console.log('Impressão finalizada - restaurando elementos do wizard');

      // Remover classe no-print de todos os elementos
      this.elementsHiddenForPrint.forEach(el => {
        el.classList.remove('no-print');
      });

      // Limpar array
      this.elementsHiddenForPrint = [];
    },

    // Configurar event listeners para impressão
    setupPrintEventListeners() {
      window.addEventListener('beforeprint', this.handleBeforePrint);
      window.addEventListener('afterprint', this.handleAfterPrint);
    },

    // Remover event listeners
    removePrintEventListeners() {
      window.removeEventListener('beforeprint', this.handleBeforePrint);
      window.removeEventListener('afterprint', this.handleAfterPrint);
    }
  },
  mounted() {
    // Configurar event listeners para impressão com Ctrl+P
    this.setupPrintEventListeners();
  },
  beforeDestroy() {
    // Remover event listeners ao destruir o componente
    this.removePrintEventListeners();
  }
}
</script>

<style lang="scss" scoped>
.step-content {
  padding: 1rem 0;
}

/* Classes dinâmicas para cores customizadas */
.text-dynamic-primary {
  color: var(--dynamic-primary) !important;
}

/* Checkboxes dinâmicos */
.checkbox-dynamic-primary {
  ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
  }

  ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
    box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
  }

  ::v-deep .custom-control-input:focus:not(:checked) ~ .custom-control-label::before {
    border-color: var(--dynamic-primary) !important;
  }

  ::v-deep .custom-control-input:not(:disabled):active ~ .custom-control-label::before {
    background-color: var(--dynamic-primary-light, var(--dynamic-primary)) !important;
    border-color: var(--dynamic-primary-light, var(--dynamic-primary)) !important;
  }

  /* Suporte para estruturas alternativas do Bootstrap Vue */
  ::v-deep .form-check-input:checked {
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
  }

  ::v-deep .form-check-input:focus {
    border-color: var(--dynamic-primary) !important;
    box-shadow: 0 0 0 0.25rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
  }

  /* Para Bootstrap 4 */
  ::v-deep input[type="checkbox"]:checked {
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
  }

  ::v-deep input[type="checkbox"]:focus {
    border-color: var(--dynamic-primary) !important;
    box-shadow: 0 0 0 0.2rem rgba(var(--dynamic-primary-rgb, 21, 100, 64), 0.25) !important;
  }
}

/* Estilos para impressão */
@media print {
  /* Configurações básicas para o conteúdo */
  .step-content {
    padding: 0 !important;
    margin: 0 !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #000 !important;
    width: 100% !important;
    max-width: none !important;
  }

  /* Ocultar APENAS elementos específicos desnecessários na impressão */
  .no-print,
  .print-button-container,
  .alert {
    display: none !important;
  }

  /* Adicionar checkboxes apenas na impressão */
  .printable-item {
    position: relative;
    padding-left: 25px !important;
    margin-bottom: 8px !important;
    display: block !important;
    line-height: 1.6 !important;
  }

  .printable-item::before {
    content: "☐";
    position: absolute;
    left: 0;
    top: 0;
    font-size: 14px !important;
    font-weight: normal !important;
    color: #000 !important;
    line-height: 1.4;
  }

  /* Ajustar títulos com checkboxes */
  h5.printable-item,
  h6.printable-item {
    font-weight: bold !important;
    margin-bottom: 12px !important;
    page-break-after: avoid;
  }

  h5.printable-item::before {
    font-size: 16px !important;
    top: 1px;
  }

  h6.printable-item::before {
    font-size: 14px !important;
    top: 1px;
  }

  /* Ajustar cards para impressão */
  .card {
    border: 1px solid #dee2e6 !important;
    box-shadow: none !important;
    margin-bottom: 15px !important;
    page-break-inside: avoid;
  }

  .card-header {
    background-color: #f8f9fa !important;
    border-bottom: 1px solid #dee2e6 !important;
    padding: 8px 12px !important;
    margin: 0 !important;
  }

  .card-body {
    padding: 12px !important;
  }

  /* Melhorar tipografia para impressão */
  h5 {
    font-size: 16px !important;
    font-weight: bold !important;
    margin-bottom: 15px !important;
    page-break-after: avoid;
  }

  h6 {
    font-size: 14px !important;
    font-weight: bold !important;
    margin-bottom: 8px !important;
    page-break-after: avoid;
  }

  /* Title styling for print */
  .step-content > h5:first-child {
    text-align: center;
    font-size: 18px !important;
    margin-bottom: 20px !important;
    padding-bottom: 10px !important;
    border-bottom: 2px solid #000 !important;
    padding-left: 0 !important;
  }

  .step-content > h5:first-child::before {
    display: none !important;
  }

  /* Ajustar espaçamentos */
  .mb-3, .my-3 {
    margin-bottom: 8px !important;
  }

  .mb-2, .my-2 {
    margin-bottom: 4px !important;
  }

  .mt-3 {
    margin-top: 8px !important;
  }

  .mt-4 {
    margin-top: 12px !important;
  }

  /* Melhorar apresentação de listas */
  .list-unstyled li {
    margin-bottom: 6px !important;
    line-height: 1.3;
  }

  /* Ajustar ícones para impressão */
  .feather {
    width: 14px !important;
    height: 14px !important;
  }

  /* Melhorar quebras de linha */
  .legal-representative-section,
  .hr-divider {
    page-break-inside: avoid;
  }

  /* Ajustar divisores */
  hr {
    border-top: 1px solid #ccc !important;
    margin: 10px 0 !important;
  }

  /* Melhorar apresentação de dados em colunas */
  .row {
    margin: 0 !important;
  }

  .col, .col-md-6, .col-md-12 {
    padding: 0 6px !important;
    margin-bottom: 6px;
  }

  /* Ajustar texto forte e enfático */
  strong {
    font-weight: bold !important;
    color: #000 !important;
  }

  /* Melhorar apresentação de textos secundários */
  .text-muted {
    color: #666 !important;
  }

  .text-primary, .text-info, .text-success, .text-danger {
    color: #000 !important;
  }

  /* Ajustar links para impressão */
  a {
    color: #000 !important;
    text-decoration: none !important;
  }

  /* Melhorar apresentação de informações de arquivo */
  .list-unstyled li small {
    display: inline !important;
    font-size: 10px !important;
    color: #666 !important;
  }

  /* Garantir que seções importantes não sejam quebradas */
  .legal-representative-section,
  .card:not(:last-child) {
    page-break-after: auto;
    page-break-inside: avoid;
  }

  /* Melhorar espaçamento entre seções de unidades vinculadas */
  .hr-divider hr {
    border-top: 1px dashed #999 !important;
    margin: 8px 0 !important;
  }

  /* Ajustar padding geral */
  .card-body > .row:last-child {
    margin-bottom: 0 !important;
  }

  /* Força quebra de página antes de seções importantes se necessário */
  .card:nth-child(4) {
    page-break-before: auto;
  }

  /* Remover margens e paddings desnecessários do body */
  body {
    margin: 0 !important;
    padding: 0 !important;
  }

  /* CSS específico para ocultar botões do wizard APENAS na impressão */
  .wizard-footer,
  .vue-form-wizard .wizard-footer,
  .form-wizard .wizard-footer,
  .wizard-nav,
  .wizard-navigation {
    display: none !important;
  }

  /* Ocultar checkboxes e elementos de formulário interativo */
  .custom-control,
  .form-check,
  .checkbox-dynamic-primary {
    display: none !important;
  }

  /* Ajustar quebras de linha para elementos com checkbox */
  span.printable-item {
    display: block !important;
    margin-bottom: 4px !important;
  }

  /* Adicionar um pouco mais de espaço após elementos com checkbox */
  .printable-item + br {
    display: none !important;
  }

  /* Melhorar espaçamento vertical para listas */
  .list-unstyled .printable-item {
    margin-bottom: 10px !important;
    padding-bottom: 2px !important;
  }

  /* Separar visualmente seções importantes */
  .card .printable-item:first-child {
    border-top: 1px solid #eee !important;
    padding-top: 8px !important;
    margin-top: 8px !important;
  }
}

/* CSS adicional para garantir que elementos com no-print sejam ocultos na impressão */
@media print {
  .no-print {
    display: none !important;
    visibility: hidden !important;
  }
}

/* Estilos para os links de download */
.download-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.2s ease;
  vertical-align: middle;

  &:hover {
    opacity: 0.7;
    text-decoration: none;
  }

  i {
    font-size: 14px;
  }
}

/* Ajustar espaçamento dos arquivos */
.file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  .file-info {
    flex: 1;
    min-width: 0;
  }

  .file-actions {
    flex-shrink: 0;
  }
}
</style>

<!-- CSS Global para no-print (sem scoped) -->
<style lang="scss">
@media print {
  .no-print,
  .wizard-card-footer.no-print,
  .vue-form-wizard .no-print {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    height: 0 !important;
    width: 0 !important;
    position: absolute !important;
    left: -9999px !important;
    overflow: hidden !important;
  }
}
</style>
